import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { accountAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

function AcceptInvitePage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user, refreshAccounts, switchAccount } = useAuth();
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState('');
  const [accepting, setAccepting] = useState(false);

  useEffect(() => {
    accountAPI.previewInvite(token)
      .then(setPreview)
      .catch((requestError) => setError(requestError.response?.data?.error || 'Invite unavailable'));
  }, [token]);

  const preserveInvite = () => {
    sessionStorage.setItem('pendingInvite', JSON.stringify({ token, createdAt: Date.now() }));
  };

  const accept = async () => {
    setAccepting(true);
    try {
      const result = await accountAPI.acceptInvite(token);
      const nextAccounts = await refreshAccounts();
      sessionStorage.removeItem('pendingInvite');
      if (nextAccounts.some((account) => account.id === result.accountId)) {
        switchAccount(result.accountId, nextAccounts);
      }
      toast.success('Shared account added');
      navigate('/dashboard');
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'Could not accept invitation');
    } finally {
      setAccepting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 px-4 py-12">
      <div className="mx-auto max-w-lg rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
        <h1 className="text-2xl font-bold text-gray-900">Shared account invitation</h1>
        {error && <p className="mt-4 rounded-xl bg-red-50 p-3 text-sm text-red-700">{error}</p>}
        {!error && !preview && <p className="mt-4 text-gray-600">Loading invitation…</p>}
        {preview && (
          <>
            <p className="mt-4 text-gray-700">
              {preview.inviterName} invited you to collaborate on <strong>{preview.accountName}</strong>.
            </p>
            {!user ? (
              <div className="mt-6 flex gap-3">
                <Link onClick={preserveInvite} to="/login" className="rounded-xl bg-blue-600 px-4 py-2 font-medium text-white">Log in</Link>
                <Link onClick={preserveInvite} to="/signup" className="rounded-xl border border-gray-300 px-4 py-2 font-medium text-gray-700">Sign up</Link>
              </div>
            ) : !user.emailVerified ? (
              <p className="mt-5 rounded-xl bg-amber-50 p-3 text-sm text-amber-800">Verify your email before accepting this invitation.</p>
            ) : (
              <button onClick={accept} disabled={accepting} className="mt-6 rounded-xl bg-blue-600 px-4 py-2 font-medium text-white disabled:opacity-50">
                {accepting ? 'Accepting…' : 'Accept invitation'}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default AcceptInvitePage;
